import { AuthEvent, AuthState } from './types'

/**
 * Pure state-machine transition function.
 *
 * Given the current state and an inbound event, returns the next state.
 * Unknown / ill-timed events return the current state unchanged so the
 * machine never crashes on a race — the runtime is responsible for not
 * dispatching nonsense, the machine is responsible for not exploding
 * if it happens.
 *
 * Invariant: pure. No timers, no logging, no side effects.
 */
export const transition = (state: AuthState, event: AuthEvent): AuthState => {
    switch (state.tag) {
        case 'idle':
            if (event.type === 'INIT_STARTED') {
                return { tag: 'initializing' }
            }
            return state

        case 'initializing':
            if (event.type === 'INIT_RESOLVED') {
                if (event.session) {
                    return { tag: 'authenticated', session: event.session }
                }
                return { tag: 'unauthenticated', reason: 'init' }
            }
            return state

        case 'unauthenticated':
            if (event.type === 'LOGIN_STARTED') {
                return { tag: 'authenticating' }
            }
            return state

        case 'authenticating':
            if (event.type === 'LOGIN_SUCCEEDED') {
                return { tag: 'authenticated', session: event.session }
            }
            if (event.type === 'LOGIN_FAILED') {
                return { tag: 'unauthenticated', reason: 'init' }
            }
            return state

        case 'authenticated':
            if (event.type === 'REFRESH_STARTED') {
                return {
                    tag: 'refreshing',
                    session: state.session,
                    refreshPromise: event.promise,
                }
            }
            if (event.type === 'LOGOUT_STARTED' || event.type === 'LOGOUT_COMPLETED') {
                return { tag: 'unauthenticated', reason: 'logout' }
            }
            if (event.type === 'SESSION_INVALIDATED') {
                return { tag: 'reauthRequired', lastError: event.error }
            }
            return state

        case 'refreshing':
            if (event.type === 'REFRESH_SUCCEEDED') {
                return { tag: 'authenticated', session: event.session }
            }
            if (event.type === 'REFRESH_FAILED_TRANSIENT') {
                // Hold the existing session; the runtime will schedule a retry.
                return { tag: 'authenticated', session: state.session }
            }
            if (event.type === 'REFRESH_FAILED_TERMINAL') {
                return { tag: 'reauthRequired', lastError: event.error }
            }
            if (event.type === 'LOGOUT_STARTED' || event.type === 'LOGOUT_COMPLETED') {
                return { tag: 'unauthenticated', reason: 'logout' }
            }
            return state

        case 'reauthRequired':
            if (event.type === 'LOGIN_STARTED') {
                return { tag: 'authenticating' }
            }
            if (event.type === 'LOGOUT_STARTED' || event.type === 'LOGOUT_COMPLETED') {
                return { tag: 'unauthenticated', reason: 'logout' }
            }
            return state
    }
}
